<template>
  <div class="forgot-pwd-container">
    <div class="forgot-pwd-card">
      <div class="brand-section">
        <div class="logo">
          <div class="logo-k">K</div>
        </div>
        <h1 class="brand-title">Kylian ERP</h1>
      </div>

      <div class="form-section">
        <div class="form-header">
          <h2 class="title">Forgot your password?</h2>
          <p class="subtitle">
            Don't worry! It happens. Please enter the email address associated
            with your account.
          </p>
        </div>

        <v-form
          ref="form"
          @submit.prevent="isLoading ? null : forgot_pwd()"
          class="forgot-pwd-form"
        >
          <div class="input-group">
            <v-text-field
              v-model="formData.email"
              label="Email Address"
              type="email"
              outlined
              :rules="emailRules"
              :disabled="isLoading"
              prepend-inner-icon="mdi-email-outline"
              class="email-input"
              hide-details="auto"
            ></v-text-field>
          </div>

          <v-btn
            type="submit"
            :loading="isLoading"
            :disabled="isLoading"
            class="submit-btn"
            large
            block
            elevation="0"
          >
            {{ isLoading ? 'Sending...' : 'Send Reset Link' }}
          </v-btn>

          <div class="back-to-login">
            <router-link to="/login" class="back-link">
              <v-icon small class="mr-1">mdi-arrow-left</v-icon>
              Back to Sign In
            </router-link>
          </div>
        </v-form>
      </div>
    </div>

    <!-- Success Dialog -->
    <v-dialog v-model="dialogSuccess" max-width="400" persistent>
      <v-card class="dialog-card">
        <v-card-text class="text-center pa-6">
          <div class="success-icon">
            <v-icon size="64" color="success">mdi-check-circle</v-icon>
          </div>
          <h3 class="dialog-title mt-4 mb-3">Email Sent Successfully!</h3>
          <p class="dialog-text">
            We've sent a password reset link to
            <strong>{{ formData.email }}</strong
            >. Please check your email and follow the instructions to reset your
            password.
          </p>
          <p class="dialog-text-small mt-3">
            Didn't receive the email? Check your spam folder or try again.
          </p>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-btn
            color="primary"
            block
            large
            @click="closeSuccessDialog"
            class="dialog-btn"
          >
            Got it, thanks!
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog v-model="dialog" max-width="400">
      <v-card class="dialog-card">
        <v-card-text class="text-center pa-6">
          <div class="error-icon">
            <v-icon size="64" color="error">mdi-alert-circle</v-icon>
          </div>
          <h3 class="dialog-title mt-4 mb-3">Oops! Something went wrong</h3>
          <p class="dialog-text">
            We couldn't send the reset email. Please check that your email
            address is correct and try again.
          </p>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-btn
            color="primary"
            block
            large
            @click="dialog = false"
            class="dialog-btn"
          >
            Try Again
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { BASE_URL } from '@/main';
import axios from 'axios';

export default {
  name: 'ForgotPassword',
  data() {
    return {
      dialog: false,
      dialogSuccess: false,
      formData: {
        email: '',
      },
      isLoading: false,
      emailRules: [
        (v) => !!v || 'Email is required',
        (v) => /.+@.+\..+/.test(v) || 'Please enter a valid email address',
      ],
    };
  },
  methods: {
    closeSuccessDialog() {
      this.dialogSuccess = false;
      this.$router.push('/login');
    },

    async forgot_pwd() {
      const valid = await this.$refs.form.validate();
      if (!valid) return;

      this.isLoading = true;

      try {
        const response = await axios.post(`${BASE_URL}forgotpassword`, {
          email: this.formData.email,
        });

        if (response.data.success) {
          this.dialogSuccess = true;
          // Auto redirect after 15 seconds
          setTimeout(() => {
            if (this.dialogSuccess) {
              this.closeSuccessDialog();
            }
          }, 15000);
        } else {
          this.dialog = true;
        }
      } catch (error) {
        console.error('Error sending reset email:', error);
        this.dialog = true;
      } finally {
        this.isLoading = false;
      }
    },
  },

  mounted() {
    document.title = 'Forgot Password | Kylian ERP';
  },
};
</script>

<style lang="scss" scoped>
.forgot-pwd-container {
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.forgot-pwd-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  width: 100%;
  max-width: 440px;
  animation: slideUp 0.6s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.brand-section {
  background: linear-gradient(135deg, #0b54c0 0%, #1e3c72 100%);
  color: white;
  text-align: center;
  padding: 40px 30px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    animation: float 6s ease-in-out infinite;
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
  }
}

.logo {
  margin-bottom: 16px;
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
}

.logo-k {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  color: white;
  backdrop-filter: blur(10px);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    background: linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.4),
      rgba(255, 255, 255, 0.1)
    );
    z-index: -1;
  }
}

.brand-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  position: relative;
  z-index: 1;
  letter-spacing: -0.5px;
}

.form-section {
  padding: 40px 30px;
}

.form-header {
  text-align: center;
  margin-bottom: 32px;
}

.title {
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 12px 0;
  line-height: 1.3;
}

.subtitle {
  font-size: 16px;
  color: #64748b;
  line-height: 1.5;
  margin: 0;
}

.forgot-pwd-form {
  width: 100%;
}

.input-group {
  margin-bottom: 24px;
}

.email-input {
  ::v-deep .v-text-field__details {
    margin-top: 8px;
  }

  ::v-deep .v-input__slot {
    border-radius: 12px !important;
    border: 2px solid #e2e8f0 !important;
    transition: all 0.3s ease;

    &:hover {
      border-color: #cbd5e0 !important;
    }
  }

  ::v-deep .v-input--is-focused .v-input__slot {
    border-color: #0b54c0 !important;
    box-shadow: 0 0 0 3px rgba(11, 84, 192, 0.1) !important;
  }

  ::v-deep .v-text-field__prepend-inner {
    margin-top: 16px;
    margin-right: 12px;

    .v-icon {
      color: #94a3b8;
    }
  }

  ::v-deep .v-input--is-focused .v-text-field__prepend-inner .v-icon {
    color: #0b54c0;
  }
}

.submit-btn {
  background: linear-gradient(135deg, #0b54c0 0%, #1e3c72 100%) !important;
  border-radius: 12px !important;
  height: 48px !important;
  font-weight: 600 !important;
  font-size: 16px !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  margin-bottom: 24px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(11, 84, 192, 0.3) !important;
  }

  &:active {
    transform: translateY(0);
  }

  ::v-deep .v-btn__content {
    color: white;
  }
}

.back-to-login {
  text-align: center;
}

.back-link {
  color: #64748b;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  transition: all 0.3s ease;

  &:hover {
    color: #0b54c0;
    text-decoration: none;
  }
}

// Dialog Styles
.dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.success-icon,
.error-icon {
  margin-bottom: 8px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.3;
}

.dialog-text {
  font-size: 16px;
  color: #64748b;
  line-height: 1.5;
  margin: 0;
}

.dialog-text-small {
  font-size: 14px;
  color: #94a3b8;
  line-height: 1.4;
  margin: 0;
}

.dialog-btn {
  background: linear-gradient(135deg, #0b54c0 0%, #1e3c72 100%) !important;
  border-radius: 12px !important;
  height: 48px !important;
  font-weight: 600 !important;
  text-transform: none !important;

  ::v-deep .v-btn__content {
    color: white;
  }
}

// Responsive Design
@media (max-width: 600px) {
  .forgot-pwd-container {
    padding: 16px;
    align-items: flex-start;
    padding-top: 40px;
  }

  .forgot-pwd-card {
    max-width: 100%;
  }

  .brand-section {
    padding: 32px 24px;
  }

  .brand-title {
    font-size: 24px;
  }

  .logo-k {
    width: 48px;
    height: 48px;
    font-size: 24px;
  }

  .form-section {
    padding: 32px 24px;
  }

  .title {
    font-size: 22px;
  }

  .subtitle {
    font-size: 15px;
  }
}

@media (max-width: 400px) {
  .brand-section {
    padding: 24px 20px;
  }

  .form-section {
    padding: 24px 20px;
  }

  .logo-k {
    width: 44px;
    height: 44px;
    font-size: 22px;
  }
}

// Remove Vuetify's default styles that might interfere
::v-deep .v-application {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    sans-serif !important;
}

::v-deep .v-text-field > .v-input__control > .v-input__slot:before,
::v-deep .v-text-field > .v-input__control > .v-input__slot:after {
  display: none;
}
</style>
